import { BackgroundAttributes } from '@redfox-thank-you/control-groups';
import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { Settings } from './settings';
import { generateCustomerInfoCSS } from './style';
import type { CustomerInfoEditProps, CustomerInfoInfoType } from './types';

/**
 * Customer Info block edit component.
 */
const CustomerInfoEdit: React.FC< CustomerInfoEditProps > = ( {
	attributes,
	setAttributes,
} ) => {
	// Create attribute manager with utility functions
	const { getAttribute, updateAttributes } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Extract settings properties with defaults using getAttribute
	const infoType = getAttribute< CustomerInfoInfoType >(
		'settings.infoType',
		'customer_name',
	);
	const customLabel = getAttribute< string >( 'settings.label.text', '' );
	const showLabel = getAttribute< boolean >( 'settings.label.show', true );

	// Info type options
	const infoTypes = [
		{
			label: __( 'Customer Name', 'redfox-thank-you' ),
			value: 'customer_name',
		},
		{
			label: __( 'Customer Email', 'redfox-thank-you' ),
			value: 'customer_email',
		},
		{
			label: __( 'Customer Phone', 'redfox-thank-you' ),
			value: 'customer_phone',
		},
		{
			label: __( 'Customer Address', 'redfox-thank-you' ),
			value: 'customer_address',
		},
		{
			label: __( 'Customer City', 'redfox-thank-you' ),
			value: 'customer_city',
		},
		{
			label: __( 'Customer State', 'redfox-thank-you' ),
			value: 'customer_state',
		},
		{
			label: __( 'Customer Zip Code', 'redfox-thank-you' ),
			value: 'customer_zip',
		},
		{
			label: __( 'Customer Country', 'redfox-thank-you' ),
			value: 'customer_country',
		},
	];

	// Get the default label for the selected info type
	const getDefaultLabel = ( infoType: string ) => {
		const selectedType = infoTypes.find(
			( type ) => type.value === infoType,
		);
		return selectedType ? `${ selectedType.label }: ` : '';
	};

	// Use custom label or default label
	const displayLabel = customLabel || getDefaultLabel( infoType );

	// Helper function to update settings (needed for RichText onChange)
	const updateSettings = ( path: string, value: string ) => {
		if ( path === 'label.text' ) {
			updateAttributes( 'settings.label.text', value );
		}
	};

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'customer-info';
		const hash = Math.random().toString( 36 ).substr( 2, 8 );
		return `redfox-thank-you-block-${ cleanName }-${ hash }`;
	};

	const [ blockClass ] = React.useState( () => generateBlockClass() );

	const blockProps = useBlockProps( {
		className: blockClass,
		style: {
			position: 'relative', // Ensure overlay positioning works correctly
		},
	} );

	// Check if any overlay is enabled across responsive breakpoints
	const hasAnyOverlayEnabled = (
		backgroundStyle: BackgroundAttributes,
	): boolean => {
		// Check responsive structure
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const breakpointValues = backgroundStyle[ breakpoint ];
			return breakpointValues?.backgroundOverlay?.enabled;
		} );
	};

	// Extract background styles for overlay rendering using getAttribute
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);

	// Check if overlay is enabled
	const overlayEnabled = hasAnyOverlayEnabled( blockBackgroundStyle );

	// Get mock data for preview
	const getMockData = ( type: CustomerInfoInfoType ): string => {
		switch ( type ) {
			case 'customer_name':
				return __( 'John Doe', 'redfox-thank-you' );
			case 'customer_email':
				return __( 'john@example.com', 'redfox-thank-you' );
			case 'customer_phone':
				return __( '123-456-7890', 'redfox-thank-you' );
			case 'customer_address':
				return __( '123 Example Street', 'redfox-thank-you' );
			case 'customer_city':
				return __( 'New York', 'redfox-thank-you' );
			case 'customer_state':
				return __( 'NY', 'redfox-thank-you' );
			case 'customer_zip':
				return __( '10001', 'redfox-thank-you' );
			case 'customer_country':
				return __( 'United States', 'redfox-thank-you' );
			default:
				return __( 'Customer Info', 'redfox-thank-you' );
		}
	};

	return (
		<>
			<StyleRenderer
				css={ generateCustomerInfoCSS( attributes, blockClass ) }
				blockId={ blockClass }
			/>
			<Settings
				updateAttributes={ updateAttributes }
				getAttribute={ getAttribute }
			/>

			<div { ...blockProps }>
				{ /* Background Overlay - responsive aware */ }
				{ overlayEnabled && (
					<div className="redfox-thank-you-customer-info__background-overlay" />
				) }

				<div className="redfox-thank-you-customer-info__container">
					{ showLabel && (
						<RichText
							tagName="strong"
							className="redfox-thank-you-customer-info__label"
							value={ displayLabel }
							onChange={ ( value: string ) =>
								updateSettings( 'label.text', value )
							}
							placeholder={ getDefaultLabel( infoType ) }
							allowedFormats={ [ 'core/bold', 'core/italic' ] }
						/>
					) }
					<span className="redfox-thank-you-customer-info__value">
						{ getMockData( infoType ) }
					</span>
				</div>
			</div>
		</>
	);
};

export { CustomerInfoEdit as Edit };
